import {
  ChangeEventHandler,
  ComponentPropsWithoutRef,
  FocusEventHandler,
  KeyboardEventHandler,
  MouseEventHandler,
  Ref,
  forwardRef,
  useEffect,
  useRef,
} from "react";
import { PopoverAnchor } from "@/kit/ui/popover";
import { Button } from "@/kit/ui/button";
import { ChevronDownIcon, ChevronUpIcon, Loading01Icon } from "@/icons";
import { inputInputVariants } from "@/kit/helpers/input";
import { PropsWithoutChildren, setRefs } from "@/utils/react";
import { cn } from "@/utils/tailwind";
import { LoadingAnchor } from "./auto-complete";
import { Unselected, useAutoComplete } from "./common";
import { AutoCompleteInputProps } from "./props";
import { useSignalValue } from "@/utils/signal";

export const AutoCompleteInput = forwardRef(function AutoCompleteInput(
  { className, children, ...props }: AutoCompleteInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const { disabled, "aria-invalid": invalid } = props;

  const inputRef = useRef<HTMLInputElement>(null);
  const { open: open_ } = useAutoComplete();
  const isOpen = useSignalValue(open_);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    } else {
      inputRef.current?.blur();
    }
  }, [isOpen]);

  return (
    <PopoverAnchor>
      <LoadingAnchor
        className={inputInputVariants({
          focused: isOpen,
          disabled,
          invalid: invalid === "true" || invalid === true,
          className,
        })}
      >
        {children}

        <div className="flex flex-1">
          <ActualInput ref={setRefs(ref, inputRef)} {...props} />
          <ToggleButton />
        </div>
      </LoadingAnchor>
    </PopoverAnchor>
  );
});

export const ActualInput = forwardRef(function ActualInput(
  {
    onChange: onChange_,
    onFocus: onFocus_,
    onBlur: onBlur_,
    onKeyDown: onKeyDown_,
    onSubmitItem,
    className,
    autoComplete = "off",
    ...props
  }: PropsWithoutChildren<AutoCompleteInputProps>,
  ref: Ref<HTMLInputElement>,
) {
  const {
    open: open_,
    input: input_,
    selected: selected_,
    list,
    onSelectItem,
    onPopItem,
  } = useAutoComplete();
  const value = useSignalValue(input_);

  const onChange: ChangeEventHandler<HTMLInputElement> = ({
    target: { value },
  }) => {
    input_.value = value;
    onChange_?.(value);
  };

  const onFocus: FocusEventHandler<HTMLInputElement> = (evt) => {
    onFocus_?.(evt);
    if (!evt.defaultPrevented) {
      open_.value = true;
    }
  };

  const onBlur: FocusEventHandler<HTMLInputElement> = (evt) => {
    onBlur_?.(evt);
    if (!evt.defaultPrevented) {
      open_.value = false;
    }
  };

  const onKeyDown: KeyboardEventHandler<HTMLInputElement> = (evt) => {
    switch (evt.key) {
      case "ArrowUp":
        evt.preventDefault();
        list.current?.selectBy(-1);
        break;

      case "ArrowDown":
        evt.preventDefault();
        list.current?.selectBy(+1);
        break;

      case "Escape":
        evt.preventDefault();
        open_.value = false;
        break;

      case "Enter": {
        const selected = selected_.value.value;
        if (selected !== Unselected) {
          evt.preventDefault();
          onSelectItem(selected);
          input_.value = "";
        } else if (onSubmitItem) {
          evt.preventDefault();
          onSubmitItem(value);
          input_.value = "";
        }
        break;
      }

      case "Backspace":
        if (!value.length) {
          evt.preventDefault();
          onPopItem();
        }
        break;
    }

    if (!evt.defaultPrevented) {
      onKeyDown_?.(evt);
    }
  };

  return (
    <input
      ref={ref}
      {...props}
      autoComplete={autoComplete}
      className={cn("flex-1 outline-hidden", className)}
      onChange={onChange}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      value={value}
    />
  );
});

export function ToggleButton({
  onClick: onClick_,
  ...buttonProps
}: ComponentPropsWithoutRef<"button">) {
  const { open: open_, loading: loading_ } = useAutoComplete();
  const isOpen = useSignalValue(open_);
  const isLoading = useSignalValue(loading_);

  if (isOpen && isLoading) {
    return <Loading01Icon className="animate-spin w-4 h-4" />;
  }

  const onClick: MouseEventHandler<HTMLButtonElement> = (evt) => {
    onClick_?.(evt);
    if (!evt.defaultPrevented) {
      open_.value = !open_.value;
    }
  };
  return (
    <Button {...buttonProps} variant="tertiary" size="xs" onClick={onClick}>
      {isOpen ? (
        <ChevronUpIcon className="w-4 h-4" />
      ) : (
        <ChevronDownIcon className="w-4 h-4" />
      )}
    </Button>
  );
}
